import React, { FC, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import axiosInstant from "../../api";
import {
    projectBriefAssetUpload,
    projectBriefAssets,
    publicProjectBriefAssetUpload,
    publicProjectBriefAssets,
} from "../../api/endpoints";
import { BriefBrandAssetsFields } from "./BriefBrandAssetsFields";
import { BRIEF_ALL_ASSET_KEYS, Brief, IBriefAssetDefaults } from "./constants";

interface Props {
    projectId?: number | string;
    publicToken?: string;
    publicBase?: string;
    /** The saved brief, if any: its snapshot wins over the brand's values. */
    brief?: Brief;
    disabled?: boolean;
}

/**
 * The brief's "Brand & Product Assets" section (TDC-1722). Loads the brand's
 * current assets + each product's disclaimers and seeds every field the brief
 * has never had (null) — a new brief opens pre-filled, a saved one keeps its
 * snapshot. Saving the brief writes changed values back to the brand/products.
 */
export const BriefBrandAssetsSection: FC<Props> = ({ projectId, publicToken, publicBase, brief, disabled }) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const [defaults, setDefaults] = useState<IBriefAssetDefaults | null>(null);

    useEffect(() => {
        let active = true;
        if (!projectId) return undefined;
        const url = publicToken
            ? publicProjectBriefAssets(publicToken, projectId, publicBase)
            : projectBriefAssets(projectId);
        axiosInstant
            .get(url)
            .then(({ data }) => {
                if (active) setDefaults(data);
            })
            .catch(() => {
                // The fields still work without defaults; they just start empty.
            });
        return () => {
            active = false;
        };
    }, [projectId, publicToken, publicBase]);

    // Seed once, when the defaults arrive.
    useEffect(() => {
        if (!defaults) return;
        BRIEF_ALL_ASSET_KEYS.forEach((key) => {
            const saved = brief ? (brief as any)[key] : null;
            if ((saved === null || saved === undefined) && !values[key] && defaults.assets[key]) {
                setFieldValue(key, defaults.assets[key]);
            }
        });
        const saved = (brief && brief.product_disclaimers) || {};
        const current: Record<string, string> = values.product_disclaimers || {};
        const next = { ...current };
        let changed = false;
        defaults.products.forEach(({ desireid }) => {
            const id = String(desireid);
            if (!(id in saved) && !next[id] && defaults.assets.product_disclaimers[id]) {
                next[id] = defaults.assets.product_disclaimers[id];
                changed = true;
            }
        });
        if (changed) setFieldValue("product_disclaimers", next);
    }, [defaults]);

    // `rmn` targets the project's retail media network instead of the supplier.
    const uploadUrlFor = (rmn: boolean) => (kind: string) => {
        if (!projectId) return undefined;
        const url = publicToken
            ? publicProjectBriefAssetUpload(publicToken, projectId, kind, publicBase)
            : projectBriefAssetUpload(projectId, kind);
        return rmn ? `${url}?brand=rmn` : url;
    };

    const brandName = defaults?.brand?.name;
    const rmnBrand = defaults?.rmn_brand;

    return (
        <>
            <BriefBrandAssetsFields
                heading={brandName ? `Brand & Product Assets — ${brandName}` : "Brand & Product Assets"}
                ownerLabel={brandName || "the brand"}
                uploadUrlFor={uploadUrlFor(false)}
                products={defaults ? defaults.products : []}
                disabled={disabled}
                collapsible
            />
            {/* An RMN order's brief also carries the retailer's own set. */}
            {rmnBrand && (
                <BriefBrandAssetsFields
                    heading={`Retailer Assets — ${rmnBrand.name}`}
                    ownerLabel={rmnBrand.name}
                    prefix="rmn_"
                    uploadUrlFor={uploadUrlFor(true)}
                    disabled={disabled}
                    collapsible
                />
            )}
        </>
    );
};
